import * as THREE from 'three';
import { Raycaster, Object3D, Vector2, Vector3, Intersection, Camera, WebGLRenderer } from 'three';

export interface RaycastResult {
  object: Object3D;
  point: Vector3;
  distance: number;
  face: THREE.Face | null;
  faceIndex: number;
  uv: Vector2;
}

export interface RaycastSettings {
  recursive?: boolean;
  filter?: (object: Object3D) => boolean;
  maxDistance?: number;
}

export class RaycastManager {
  private _raycaster: Raycaster;
  private _mousePosition: Vector2;
  private _isEnabled: boolean = false;
  private _lastIntersection: RaycastResult | null = null;
  private _hoveredObject: Object3D | null = null;
  private _selectedObject: Object3D | null = null;

  // 콜백 함수들
  public _onObjectHovered: ((object: Object3D | null) => void) | null = null;
  public _onObjectSelected: ((object: Object3D | null) => void) | null = null;
  public _onIntersectionChanged: ((result: RaycastResult | null) => void) | null = null;

  constructor() {
    this._raycaster = new Raycaster();
    this._mousePosition = new Vector2();
  }

  /**
   * 레이캐스팅 활성화
   */
  public enable(): void {
    this._isEnabled = true;
  }

  /**
   * 레이캐스팅 비활성화
   */
  public disable(): void {
    this._isEnabled = false;
    this.clearHoveredObject();
    this.clearSelectedObject();
  }

  /**
   * 마우스 위치 설정
   */
  public setMousePosition(x: number, y: number): void {
    this._mousePosition.set(x, y);
  }

  /**
   * 마우스 위치를 정규화된 좌표로 설정
   */
  public setNormalizedMousePosition(x: number, y: number): void {
    this._mousePosition.set(x, y);
  }

  /**
   * 마우스 위치를 화면 좌표로 설정
   */
  public setScreenMousePosition(x: number, y: number, renderer: WebGLRenderer): void {
    const rect = renderer.domElement.getBoundingClientRect();
    const normalizedX = ((x - rect.left) / rect.width) * 2 - 1;
    const normalizedY = -((y - rect.top) / rect.height) * 2 + 1;
    this._mousePosition.set(normalizedX, normalizedY);
  }

  /**
   * 레이캐스팅 수행
   */
  public raycast(
    camera: Camera,
    objects: Object3D[],
    settings: RaycastSettings = {},
  ): RaycastResult[] {
    if (!this._isEnabled) return [];

    const { recursive = true, filter, maxDistance = Infinity } = settings;

    // 필터링된 오브젝트들
    let targetObjects = objects;
    if (filter) {
      targetObjects = objects.filter(filter);
    }

    // 레이캐스팅 수행
    this._raycaster.setFromCamera(this._mousePosition, camera);
    this._raycaster.far = maxDistance;

    const intersections = this._raycaster.intersectObjects(targetObjects, recursive);

    // 결과를 RaycastResult 형식으로 변환
    return intersections.map((intersection) => ({
      object: intersection.object,
      point: intersection.point,
      distance: intersection.distance,
      face: intersection.face || null,
      faceIndex: intersection.faceIndex || 0,
      uv: intersection.uv || new Vector2(),
    }));
  }

  /**
   * 단일 오브젝트에 대한 레이캐스팅
   */
  public raycastObject(
    camera: Camera,
    object: Object3D,
    settings: RaycastSettings = {},
  ): RaycastResult[] {
    return this.raycast(camera, [object], settings);
  }

  /**
   * 첫 번째 교차점 찾기
   */
  public findFirstIntersection(
    camera: Camera,
    objects: Object3D[],
    settings: RaycastSettings = {},
  ): RaycastResult | null {
    const results = this.raycast(camera, objects, settings);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * 특정 타입의 오브젝트만 레이캐스팅
   */
  public raycastByType<T extends Object3D>(
    camera: Camera,
    objects: Object3D[],
    type: new () => T,
    settings: RaycastSettings = {},
  ): RaycastResult[] {
    const filteredObjects = objects.filter((obj) => obj instanceof type);
    return this.raycast(camera, filteredObjects, settings);
  }

  /**
   * 이름으로 오브젝트 찾기
   */
  public raycastByName(
    camera: Camera,
    objects: Object3D[],
    name: string,
    settings: RaycastSettings = {},
  ): RaycastResult[] {
    const filteredObjects = objects.filter((obj) =>
      obj.name.toLowerCase().includes(name.toLowerCase()),
    );
    return this.raycast(camera, filteredObjects, settings);
  }

  /**
   * 사용자 데이터로 오브젝트 찾기
   */
  public raycastByUserData(
    camera: Camera,
    objects: Object3D[],
    key: string,
    value: any,
    settings: RaycastSettings = {},
  ): RaycastResult[] {
    const filteredObjects = objects.filter((obj) => obj.userData && obj.userData[key] === value);
    return this.raycast(camera, filteredObjects, settings);
  }

  /**
   * 호버된 오브젝트 업데이트
   */
  public updateHoveredObject(
    camera: Camera,
    objects: Object3D[],
    settings: RaycastSettings = {},
  ): void {
    if (!this._isEnabled) return;

    const intersection = this.findFirstIntersection(camera, objects, settings);

    if (intersection && intersection.object !== this._hoveredObject) {
      this.setHoveredObject(intersection.object);
    } else if (!intersection && this._hoveredObject) {
      this.clearHoveredObject();
    }

    // 교차점 변경 감지
    if (intersection !== this._lastIntersection) {
      this._lastIntersection = intersection;
      if (this._onIntersectionChanged) {
        this._onIntersectionChanged(intersection);
      }
    }
  }

  /**
   * 호버된 오브젝트 설정
   */
  public setHoveredObject(object: Object3D): void {
    if (this._hoveredObject === object) return;

    this._hoveredObject = object;

    if (this._onObjectHovered) {
      this._onObjectHovered(object);
    }
  }

  /**
   * 호버된 오브젝트 제거
   */
  public clearHoveredObject(): void {
    if (this._hoveredObject === null) return;

    this._hoveredObject = null;

    if (this._onObjectHovered) {
      this._onObjectHovered(null);
    }
  }

  /**
   * 선택된 오브젝트 설정
   */
  public setSelectedObject(object: Object3D | null): void {
    if (this._selectedObject === object) return;

    this._selectedObject = object;

    if (this._onObjectSelected) {
      this._onObjectSelected(object);
    }
  }

  /**
   * 선택된 오브젝트 제거
   */
  public clearSelectedObject(): void {
    this.setSelectedObject(null);
  }

  /**
   * 마우스 클릭으로 오브젝트 선택
   */
  public selectObjectOnClick(
    camera: Camera,
    objects: Object3D[],
    settings: RaycastSettings = {},
  ): Object3D | null {
    const intersection = this.findFirstIntersection(camera, objects, settings);

    if (intersection) {
      this.setSelectedObject(intersection.object);
      return intersection.object;
    } else {
      this.clearSelectedObject();
      return null;
    }
  }

  /**
   * 특정 포인트에서 레이캐스팅
   */
  public raycastFromPoint(
    origin: Vector3,
    direction: Vector3,
    objects: Object3D[],
    settings: RaycastSettings = {},
  ): RaycastResult[] {
    if (!this._isEnabled) return [];

    const { recursive = true, filter, maxDistance = Infinity } = settings;

    // 필터링된 오브젝트들
    let targetObjects = objects;
    if (filter) {
      targetObjects = objects.filter(filter);
    }

    // 레이캐스터 설정
    this._raycaster.set(origin, direction);
    this._raycaster.far = maxDistance;

    const intersections = this._raycaster.intersectObjects(targetObjects, recursive);

    // 결과를 RaycastResult 형식으로 변환
    return intersections.map((intersection) => ({
      object: intersection.object,
      point: intersection.point,
      distance: intersection.distance,
      face: intersection.face || null,
      faceIndex: intersection.faceIndex || 0,
      uv: intersection.uv || new Vector2(),
    }));
  }

  /**
   * 충돌 감지
   */
  public checkCollision(
    origin: Vector3,
    direction: Vector3,
    objects: Object3D[],
    maxDistance: number = Infinity,
  ): boolean {
    const results = this.raycastFromPoint(origin, direction, objects, { maxDistance });
    return results.length > 0;
  }

  /**
   * 가장 가까운 오브젝트 찾기
   */
  public findClosestObject(
    camera: Camera,
    objects: Object3D[],
    settings: RaycastSettings = {},
  ): RaycastResult | null {
    const results = this.raycast(camera, objects, settings);

    if (results.length === 0) return null;

    // 거리순으로 정렬하여 가장 가까운 것 반환
    return results.reduce((closest, current) =>
      current.distance < closest.distance ? current : closest,
    );
  }

  /**
   * 특정 거리 내의 오브젝트들 찾기
   */
  public findObjectsInRange(
    camera: Camera,
    objects: Object3D[],
    maxDistance: number,
    settings: RaycastSettings = {},
  ): RaycastResult[] {
    const results = this.raycast(camera, objects, { ...settings, maxDistance });
    return results.filter((result) => result.distance <= maxDistance);
  }

  // Getter 메서드들
  public getRaycaster(): Raycaster {
    return this._raycaster;
  }

  public getMousePosition(): Vector2 {
    return this._mousePosition.clone();
  }

  public isEnabled(): boolean {
    return this._isEnabled;
  }

  public getHoveredObject(): Object3D | null {
    return this._hoveredObject;
  }

  public getSelectedObject(): Object3D | null {
    return this._selectedObject;
  }

  public getLastIntersection(): RaycastResult | null {
    return this._lastIntersection;
  }

  /**
   * 리소스 정리
   */
  public dispose(): void {
    this.disable();
    this._raycaster = null as any;
    this._mousePosition = null as any;
  }
}
